import React from "react";
import ClassificationBadge from "./ClassificationBadge";
import type { ComparisonEntry } from "../../hooks/useReporterComparisonQueries";

// OP bars — 4 bars, filled count based on how many operating points are positive
const OpBars: React.FC<{ operatingPoint: ComparisonEntry["ai"]["operating_point"] }> = ({
  operatingPoint,
}) => {
  // Count filled bars (ordered low→high confidence)
  const ops = [
    operatingPoint.high_sensitivity,
    operatingPoint.sensitivity_biased,
    operatingPoint.balanced,
    operatingPoint.high_specificity,
  ];
  const filled = ops.filter(Boolean).length;

  return (
    <div className="flex items-end gap-[2px]">
      {[1, 2, 3, 4].map((idx) => (
        <span
          key={idx}
          className={`w-[3px] rounded-xs transition-all ${
            idx <= filled ? "h-2 bg-primary" : "h-1.5 bg-border-strong opacity-40"
          }`}
        />
      ))}
    </div>
  );
};

interface ComparisonFindingRowProps {
  entry: ComparisonEntry;
  classification: "agreement" | "ai_only" | "report_only";
  onClick?: () => void;
}

const ComparisonFindingRow: React.FC<ComparisonFindingRowProps> = ({
  entry,
  classification,
  onClick,
}) => {
  const findingName = entry.finding.split("/").pop()?.replace(/_/g, " ") ?? entry.finding;

  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-2.5 hover:bg-surface-2 border-l-2 border-l-transparent hover:border-l-primary/50 transition-colors"
    >
      <div className="flex items-center gap-2 mb-1">
        <ClassificationBadge classification={classification} />
        <span className="text-[13px] font-medium text-fg capitalize truncate">{findingName}</span>
        <div className="ml-auto shrink-0">
          <OpBars operatingPoint={entry.ai.operating_point} />
        </div>
      </div>

      <div className="ml-[60px] text-[11px] text-fg-muted space-y-0.5">
        <div>AI: {entry.ai.probability.toFixed(3)}</div>
        {entry.report.excerpt ? (
          <div className="text-fg-muted italic truncate">
            Report: &ldquo;{entry.report.excerpt}&rdquo;
          </div>
        ) : (
          <div>Report: &mdash;</div>
        )}
      </div>
    </button>
  );
};

export default ComparisonFindingRow;
